import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface SystemStatus {
  api: 'online' | 'offline' | 'loading';
  database: 'connected' | 'disconnected' | 'loading';
  functions: 'deployed' | 'error' | 'loading';
  lastCheck: string;
}

interface Statistics {
  totalLicenses: number;
  activeLicenses: number;
  totalActivations: number;
  apiCalls24h: number;
}

interface DetailedHealthInfo {
  version: string;
  uptime: number;
  region: string;
  nodeVersion: string;
  databaseId: string;
  timestamp: string;
  projectId?: string;
  environment?: string;
  endpoints?: Array<{
    method: string;
    path: string;
    description: string;
  }>;
}

export function Dashboard() {
  const { currentUser } = useAuth();
  const [systemStatus, setSystemStatus] = useState<SystemStatus>({
    api: 'loading',
    database: 'loading',
    functions: 'loading',
    lastCheck: new Date().toLocaleString('nl-NL')
  });
  const [statistics, setStatistics] = useState<Statistics>({
    totalLicenses: 0,
    activeLicenses: 0,
    totalActivations: 0,
    apiCalls24h: 0
  });
  const [healthInfo, setHealthInfo] = useState<DetailedHealthInfo | null>(null);
  const [loading, setLoading] = useState(false);

  const getAuthHeaders = async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get ID token:', error);
      }
    }
    
    return headers;
  };

  const fetchStatistics = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('https://license.s3e.nl/admin/stats', {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setStatistics({
            totalLicenses: data.data.totalLicenses || 0,
            activeLicenses: data.data.activeLicenses || 0,
            totalActivations: data.data.activeSessions || 0,
            apiCalls24h: data.data.apiCalls24h || 0
          });
        }
      } else {
        console.warn('Stats API returned error:', response.status);
        setStatistics({
          totalLicenses: 0,
          activeLicenses: 0,
          totalActivations: 0,
          apiCalls24h: 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      setStatistics({
        totalLicenses: 0,
        activeLicenses: 0,
        totalActivations: 0,
        apiCalls24h: 0
      });
    }
  };

  const checkSystemStatus = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://license.s3e.nl/health');
      if (response.ok) {
        const data = await response.json();
        setSystemStatus(prev => ({
          ...prev,
          api: 'online',
          database: data.database === 'connected' ? 'connected' : 'disconnected',
          functions: 'deployed',
          lastCheck: new Date().toLocaleString('nl-NL')
        }));
        
        try {
          const detailedResponse = await fetch('https://license.s3e.nl/admin/health', {
            headers: await getAuthHeaders()
          });
          if (detailedResponse.ok) {
            const detailedData = await detailedResponse.json();
            setHealthInfo({
              version: detailedData.version || '1.0.0',
              uptime: detailedData.uptime || 0,
              region: detailedData.region || 'europe-west1',
              nodeVersion: detailedData.nodeVersion || 'Unknown',
              databaseId: detailedData.databaseId || 'default',
              timestamp: detailedData.timestamp || new Date().toISOString(),
              projectId: detailedData.projectId,
              environment: detailedData.environment,
              endpoints: detailedData.endpoints
            });
          }
        } catch (error) {
          setHealthInfo({
            version: '1.0.0',
            uptime: 0,
            region: 'europe-west1',
            nodeVersion: 'Node.js',
            databaseId: 'default',
            timestamp: new Date().toISOString(),
            projectId: 'default',
            environment: 'development',
            endpoints: []
          });
        }

        await fetchStatistics();
      } else {
        setSystemStatus(prev => ({
          ...prev,
          api: 'offline',
          database: 'disconnected',
          functions: 'error',
          lastCheck: new Date().toLocaleString('nl-NL')
        }));
      }
    } catch (error) {
      setSystemStatus(prev => ({
        ...prev,
        api: 'offline',
        database: 'disconnected',
        functions: 'error',
        lastCheck: new Date().toLocaleString('nl-NL')
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkSystemStatus();
    const interval = setInterval(checkSystemStatus, 30000);
    return () => clearInterval(interval);
  }, [currentUser]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
      case 'connected':
      case 'deployed':
        return 'bg-green-100 text-green-800';
      case 'offline':
      case 'disconnected':
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'loading':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
      case 'connected':
      case 'deployed':
        return '✅';
      case 'offline':
      case 'disconnected':
      case 'error':
        return '❌';
      case 'loading':
        return '⏳';
      default:
        return '❓';
    }
  };

  const formatUptime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}u ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
            Dashboard
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Welkom terug! 👋
          </p>
          <p className="text-sm text-gray-500">
            Hier is wat er vandaag gebeurt met je licentieserver
          </p>
        </div>

        {/* System Status Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-12 translate-x-12 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">API Status</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(systemStatus.api)}`}>
                      {getStatusIcon(systemStatus.api)} {systemStatus.api === 'online' ? 'ONLINE' : systemStatus.api === 'offline' ? 'OFFLINE' : 'LADEN'}
                    </span>
                  </div>
                </div>
                <div className="text-3xl">🌐</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-green-100 to-emerald-200 rounded-full -translate-y-12 translate-x-12 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">Database</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(systemStatus.database)}`}>
                      {getStatusIcon(systemStatus.database)} {systemStatus.database === 'connected' ? 'VERBONDEN' : systemStatus.database === 'disconnected' ? 'NIET VERBONDEN' : 'LADEN'}
                    </span>
                  </div>
                </div>
                <div className="text-3xl">🗄️</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-200 rounded-full -translate-y-12 translate-x-12 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">Functies</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(systemStatus.functions)}`}>
                      {getStatusIcon(systemStatus.functions)} {systemStatus.functions === 'deployed' ? 'ACTIEF' : systemStatus.functions === 'error' ? 'FOUT' : 'LADEN'}
                    </span>
                  </div>
                </div>
                <div className="text-3xl">⚡</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-100 to-yellow-200 rounded-full -translate-y-12 translate-x-12 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-600">Laatste Check</p>
                  <p className="text-sm font-bold text-gray-900 mt-2">{systemStatus.lastCheck}</p>
                  <button
                    onClick={checkSystemStatus}
                    disabled={loading}
                    className="text-xs text-blue-600 hover:text-blue-800 mt-1 disabled:opacity-50 transition-colors duration-200"
                  >
                    {loading ? '⏳ Vernieuwen...' : '🔄 Vernieuwen'}
                  </button>
                </div>
                <div className="text-3xl">⏰</div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-600 text-sm font-semibold">Totaal Licenties</p>
                  <p className="text-3xl font-bold text-gray-900">{statistics.totalLicenses}</p>
                </div>
                <div className="text-4xl opacity-80">📄</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-100 to-emerald-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-600 text-sm font-semibold">Actieve Licenties</p>
                  <p className="text-3xl font-bold text-gray-900">{statistics.activeLicenses}</p>
                </div>
                <div className="text-4xl opacity-80">✅</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-100 to-pink-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-600 text-sm font-semibold">Actieve Sessies</p>
                  <p className="text-3xl font-bold text-gray-900">{statistics.totalActivations}</p>
                </div>
                <div className="text-4xl opacity-80">🔑</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100 to-yellow-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-600 text-sm font-semibold">API Calls (24u)</p>
                  <p className="text-3xl font-bold text-gray-900">{statistics.apiCalls24h}</p>
                </div>
                <div className="text-4xl opacity-80">📊</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
            <div className="relative z-10">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">⚡</span>
                Snelle Acties
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Link to="/licenses" className="bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-blue-700 p-4 rounded-xl text-sm font-medium transition-all duration-200 flex flex-col items-center space-y-2 border border-blue-200 shadow-sm hover:shadow-md">
                  <span className="text-2xl">➕</span>
                  <span>Licentie Aanmaken</span>
                </Link>
                <Link to="/activations" className="bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 text-green-700 p-4 rounded-xl text-sm font-medium transition-all duration-200 flex flex-col items-center space-y-2 border border-green-200 shadow-sm hover:shadow-md">
                  <span className="text-2xl">👥</span>
                  <span>Activaties Bekijken</span>
                </Link>
                <Link to="/logs" className="bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 text-purple-700 p-4 rounded-xl text-sm font-medium transition-all duration-200 flex flex-col items-center space-y-2 border border-purple-200 shadow-sm hover:shadow-md">
                  <span className="text-2xl">📊</span>
                  <span>Logs Bekijken</span>
                </Link>
                <Link to="/settings" className="bg-gradient-to-r from-orange-50 to-yellow-50 hover:from-orange-100 hover:to-yellow-100 text-orange-700 p-4 rounded-xl text-sm font-medium transition-all duration-200 flex flex-col items-center space-y-2 border border-orange-200 shadow-sm hover:shadow-md">
                  <span className="text-2xl">⚙️</span>
                  <span>Instellingen</span>
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-100 to-pink-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
            <div className="relative z-10">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🔗</span>
                Systeem Links
              </h3>
              <div className="space-y-3">
                <Link
                  to="/health"
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 rounded-xl transition-all duration-200 border border-gray-200 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">🏥</span>
                    <span className="font-medium">Health Dashboard</span>
                  </div>
                  <span className="text-gray-400">→</span>
                </Link>
                <a
                  href="https://console.firebase.google.com/project/s3e-lisencing-server"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 rounded-xl transition-all duration-200 border border-gray-200 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">🔥</span>
                    <span className="font-medium">Firebase Console</span>
                  </div>
                  <span className="text-gray-400">↗</span>
                </a>
                <a
                  href="https://console.firebase.google.com/project/s3e-lisencing-server/firestore"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 rounded-xl transition-all duration-200 border border-gray-200 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">🗄️</span>
                    <span className="font-medium">Firestore Database</span>
                  </div>
                  <span className="text-gray-400">↗</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* API Information */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden mb-8">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
          <div className="relative z-10">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🌐</span>
              API Informatie
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Endpoints</h4>
                <div className="space-y-2 text-sm">
                  {healthInfo?.endpoints && healthInfo.endpoints.length > 0 ? (
                    healthInfo.endpoints.map((endpoint, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">{endpoint.method.toUpperCase()}</span>
                        <code className="text-gray-600">{endpoint.path}</code>
                      </div>
                    ))
                  ) : (
                    // Fallback endpoints als de server geen endpoints teruggeeft
                    [
                      { method: 'GET', path: '/health' },
                      { method: 'POST', path: '/register' },
                      { method: 'POST', path: '/activate' },
                      { method: 'POST', path: '/deactivate' },
                      { method: 'POST', path: '/validate' },
                      { method: 'GET', path: '/admin/stats' },
                      { method: 'GET', path: '/admin/health' },
                      { method: 'GET', path: '/admin/activations' },
                      { method: 'POST', path: '/admin/export' },
                      { method: 'POST', path: '/admin/cleanup' }
                    ].map((endpoint, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">{endpoint.method}</span>
                        <code className="text-gray-600">{endpoint.path}</code>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Basis URL</h4>
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl border border-gray-200">
                  <code className="text-sm text-gray-800 break-all">
                    https://license.s3e.nl
                  </code>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Gebruik deze URL als basis voor alle API calls vanuit je Revit plugin.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Health Information */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-100 to-emerald-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
          <div className="relative z-10">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🔍</span>
              Gedetailleerde Health Informatie
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Versie</h4>
                <p className="text-sm text-gray-600">{healthInfo?.version}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Uptime</h4>
                <p className="text-sm text-gray-600">{formatUptime(healthInfo?.uptime || 0)}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Regio</h4>
                <p className="text-sm text-gray-600">{healthInfo?.region}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Node Versie</h4>
                <p className="text-sm text-gray-600">{healthInfo?.nodeVersion}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Database ID</h4>
                <p className="text-sm text-gray-600">{healthInfo?.databaseId}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Timestamp</h4>
                <p className="text-sm text-gray-600">{healthInfo?.timestamp}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Project ID</h4>
                <p className="text-sm text-gray-600">{healthInfo?.projectId}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Omgeving</h4>
                <p className="text-sm text-gray-600">{healthInfo?.environment}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright Footer */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          © 2025 S3e
        </p>
      </div>
    </div>
  );
}

export default Dashboard; 